"use client";

import { useEffect, useMemo, useRef, useState } from "react";

export type TodoItem = {
  id: string;
  title: string;
  done: boolean;
};

function createId(): string {
  return Math.random().toString(36).slice(2, 9);
}

const STORAGE_KEY = "ee.todo.items.v1";
const TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

type StoredTodo = { items: TodoItem[]; expiresAt: number };

function readFromStorage(): TodoItem[] {
  try {
    if (typeof window === "undefined") return [];
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Backward compatibility: older versions stored an array directly
    if (Array.isArray(parsed)) return parsed as TodoItem[];
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as StoredTodo).items)
    ) {
      const expiresAt = (parsed as StoredTodo).expiresAt;
      if (typeof expiresAt === "number" && expiresAt > Date.now()) {
        return (parsed as StoredTodo).items;
      }
      // Expired
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }
  } catch {}
  return [];
}

function writeToStorage(items: TodoItem[]): void {
  try {
    if (typeof window === "undefined") return;
    const payload: StoredTodo = {
      items,
      // Rolling TTL: extend on each change
      expiresAt: Date.now() + TTL_MS,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
}

export default function Todo() {
  const [items, setItems] = useState<TodoItem[]>(() => readFromStorage());
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Persist to localStorage with TTL
  useEffect(() => {
    writeToStorage(items);
  }, [items]);

  const visibleItems = useMemo(() => items, [items]);

  function addItem(title: string) {
    const trimmed = title.trim();
    if (!trimmed) return;
    setItems((prev) => [
      { id: createId(), title: trimmed, done: false },
      ...prev,
    ]);
  }

  function toggleItem(id: string) {
    setItems((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const value = (inputRef.current?.value ?? "").trim();
    if (value) {
      addItem(value);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-3">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-2"
        aria-label="Add todo"
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Add a task and press Enter"
          className="h-10 w-full sm:flex-1 rounded-md border border-[--color-border] bg-background px-3 text-[color:var(--color-foreground)] placeholder-[color:var(--color-muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-accent]"
          aria-label="New task"
        />
        <button
          type="submit"
          className="h-10 w-full sm:w-auto px-3 rounded-md bg-[--color-accent] text-[--color-accent-foreground] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-accent]"
          aria-label="Add task"
        >
          Add
        </button>
      </form>

      <ul className="flex-1 overflow-auto divide-y divide-[--color-border] rounded-md border border-[--color-border] bg-card">
        {visibleItems.length === 0 ? (
          <li className="p-4 text-sm text-[color:var(--color-muted-foreground)]">
            No tasks
          </li>
        ) : (
          visibleItems.map((t) => (
            <li key={t.id} className="group flex items-center gap-3 p-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={t.done}
                  onChange={() => toggleItem(t.id)}
                  aria-label={t.title}
                  className="h-4 w-4 accent-[--color-accent]"
                />
                <span
                  className={t.done ? "line-through opacity-70" : undefined}
                >
                  {t.title}
                </span>
              </label>
              <button
                type="button"
                onClick={() => removeItem(t.id)}
                className="ml-auto h-8 px-2 rounded-md bg-surface hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-accent] opacity-0 group-hover:opacity-100"
                aria-label={`Delete ${t.title}`}
                title="Delete"
              >
                Delete
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
